import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Briefcase, Clock, Star, Calendar, MapPin, CheckCircle, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { servicesAPI, reviewsAPI, bookingsAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

export default function BookingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [service, setService] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const [sRes, rRes] = await Promise.all([
          servicesAPI.getById(id),
          reviewsAPI.getForService(id).catch(() => ({ data: [] })),
        ]);
        setService(sRes.data);
        setReviews(Array.isArray(rRes.data) ? rRes.data : []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id]);

  useEffect(() => {
    if (!selectedDate) return;
    const fetchSlots = async () => {
      setLoadingSlots(true);
      try {
        const res = await servicesAPI.getSlots(id, selectedDate);
        setSlots(res.data.slots || []);
      } catch (e) { console.error(e); }
      finally { setLoadingSlots(false); }
    };
    fetchSlots();
  }, [selectedDate, id]);

  const handleBook = async () => {
    if (!user) { navigate('/login'); return; }
    if (!selectedDate || !selectedSlot) { toast.error('Select a date and time slot'); return; }
    setBooking(true);
    try {
      const res = await bookingsAPI.create({ service_id: id, date: selectedDate, time_slot: selectedSlot, notes });
      toast.success('Booking confirmed!');
      navigate('/orders');
    } catch (e) { toast.error(e.response?.data?.detail || 'Booking failed'); }
    finally { setBooking(false); }
  };

  // Generate next 7 days
  const dates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() + i);
    return { value: d.toISOString().split('T')[0], label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }), isToday: i === 0 };
  });

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!service) return <div className="min-h-screen flex items-center justify-center"><p>Service not found</p></div>;

  return (
    <div className="min-h-screen bg-zinc-50 pb-8">
      {/* Header Image */}
      <div className="relative h-48 bg-gradient-to-br from-violet-500 to-purple-600">
        <button onClick={() => navigate(-1)} className="absolute top-4 left-4 p-2 bg-white/20 backdrop-blur rounded-full text-white" data-testid="back-btn"><ArrowLeft size={20} /></button>
        {service.image_url && <img src={service.image_url} alt={service.name} className="w-full h-full object-cover opacity-30" />}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <span className="px-3 py-1 bg-violet-400/50 text-white rounded-full text-xs font-semibold mb-2 inline-block">{service.category}</span>
          <h1 className="font-display font-bold text-2xl text-white" data-testid="service-name">{service.name}</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Info */}
        <div className="bg-white rounded-2xl border border-zinc-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Clock size={16} className="text-zinc-400" />
                <span className="text-sm text-zinc-600">{service.duration_minutes} min</span>
              </div>
              {service.reviews_count > 0 && (
                <div className="flex items-center gap-1">
                  <Star size={16} className="text-yellow-500 fill-yellow-500" />
                  <span className="text-sm text-zinc-600">{service.rating?.toFixed(1)} ({service.reviews_count} reviews)</span>
                </div>
              )}
            </div>
            <span className="font-display font-bold text-2xl text-violet-600">{formatCurrency(service.price)}</span>
          </div>
          <p className="text-zinc-600 text-sm">{service.description}</p>
          <p className="text-xs text-zinc-400 mt-2">Provider: {service.vendor_name}</p>
        </div>

        {/* Date Selection */}
        <div className="bg-white rounded-2xl border border-zinc-200 p-6">
          <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2"><Calendar size={18} className="text-violet-500" /> Select Date</h3>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {dates.map((d) => (
              <button key={d.value} onClick={() => { setSelectedDate(d.value); setSelectedSlot(''); }}
                className={`px-4 py-3 rounded-xl border text-center min-w-[90px] transition-all ${selectedDate === d.value ? 'bg-violet-600 text-white border-violet-600' : 'bg-zinc-50 border-zinc-200 hover:border-violet-300'}`}
                data-testid={`date-${d.value}`}>
                <p className="text-xs font-medium">{d.isToday ? 'Today' : d.label.split(',')[0]?.split(' ')[0]}</p>
                <p className="font-semibold text-sm">{d.label.split(' ').slice(1).join(' ')}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Time Slots */}
        {selectedDate && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-zinc-200 p-6">
            <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2"><Clock size={18} className="text-violet-500" /> Available Slots</h3>
            {loadingSlots ? (
              <div className="grid grid-cols-4 gap-2">{[...Array(8)].map((_, i) => <div key={i} className="h-12 bg-zinc-100 rounded-lg skeleton" />)}</div>
            ) : slots.length > 0 ? (
              <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                {slots.map((slot) => (
                  <button key={slot.time} onClick={() => slot.available && setSelectedSlot(slot.time)} disabled={!slot.available}
                    className={`py-3 px-2 rounded-lg text-sm font-medium transition-all ${selectedSlot === slot.time ? 'bg-violet-600 text-white' : slot.available ? 'bg-zinc-50 border border-zinc-200 hover:border-violet-300' : 'bg-zinc-100 text-zinc-300 cursor-not-allowed line-through'}`}
                    data-testid={`slot-${slot.time}`}>
                    {slot.time}
                  </button>
                ))}
              </div>
            ) : <p className="text-zinc-500 text-sm">No available slots for this date</p>}
          </motion.div>
        )}

        {/* Notes + Book */}
        {selectedSlot && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-zinc-200 p-6">
            <Textarea placeholder="Any special notes or requests? (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="mb-4" data-testid="booking-notes" />
            <div className="flex items-center justify-between p-4 bg-violet-50 border border-violet-200 rounded-xl mb-4">
              <div>
                <p className="text-sm text-zinc-600">{selectedDate} at {selectedSlot}</p>
                <p className="text-xs text-zinc-400">{service.duration_minutes} minutes with {service.vendor_name}</p>
              </div>
              <p className="font-display font-bold text-xl text-violet-600">{formatCurrency(service.price)}</p>
            </div>
            <Button onClick={handleBook} disabled={booking} className="w-full bg-violet-600 text-white hover:bg-violet-700 py-6 text-base font-bold rounded-xl" data-testid="confirm-booking-btn">
              <CheckCircle size={18} className="mr-2" /> {booking ? 'Booking...' : 'Confirm Booking'}
            </Button>
          </motion.div>
        )}

        {/* Reviews */}
        {reviews.length > 0 && (
          <div className="bg-white rounded-2xl border border-zinc-200 p-6">
            <h3 className="font-display font-semibold text-lg mb-4">Reviews ({reviews.length})</h3>
            <div className="space-y-4">
              {reviews.slice(0, 5).map((r) => (
                <div key={r.id} className="border-b border-zinc-100 pb-3 last:border-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 bg-violet-100 rounded-full flex items-center justify-center text-xs font-bold text-violet-600">{r.user_name?.[0]}</div>
                    <span className="text-sm font-medium">{r.user_name}</span>
                    <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={12} className={i < r.rating ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-200'} />)}</div>
                  </div>
                  <p className="text-sm text-zinc-600 ml-9">{r.comment}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
